import React, { useEffect, useState } from 'react'
import { Loader2Icon, SearchIcon, UserPlusIcon, XIcon } from 'lucide-react'
import type { InboxEntityLinkKey } from '../../lib/inboxEntityLinks'

export interface UnlinkedSearchResult {
  id: string
  name: string
  contacts: any[]
}

interface InboxUnlinkedLinkModalProps {
  isOpen: boolean
  onClose: () => void
  conversationId: string
  channel: 'whatsapp' | 'sms' | 'email'
  contactInfo: string
  assigning: boolean
  onSearch: (type: 'customer' | 'lead', term: string) => Promise<UnlinkedSearchResult[]>
  onAttachOnly: (record: { id: string }, type: 'customer' | 'lead') => void
  onSaveAsContact: (record: UnlinkedSearchResult, type: 'customer' | 'lead') => void
  onLinkEntity: (key: InboxEntityLinkKey) => void
  onCreateNew: (type: 'customer' | 'lead') => void
}

export function InboxUnlinkedLinkModal({
  isOpen,
  onClose,
  channel,
  contactInfo,
  assigning,
  onSearch,
  onAttachOnly,
  onSaveAsContact,
  onLinkEntity,
  onCreateNew,
}: InboxUnlinkedLinkModalProps) {
  const [assignTab, setAssignTab] = useState<'existing' | 'new'>('existing')
  const [assignType, setAssignType] = useState<'customer' | 'lead'>('customer')
  const [searchTerm, setSearchTerm] = useState('')
  const [results, setResults] = useState<UnlinkedSearchResult[]>([])
  const [searching, setSearching] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setAssignTab('existing')
      setAssignType('customer')
      setSearchTerm('')
      setResults([])
    }
  }, [isOpen])

  if (!isOpen) return null

  async function runSearch() {
    if (!searchTerm.trim()) {
      setResults([])
      return
    }
    setSearching(true)
    try {
      const rows = await onSearch(assignType, searchTerm)
      setResults(rows)
    } finally {
      setSearching(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <UserPlusIcon className="w-4 h-4 text-amber-700" />
            <h3 className="text-sm font-semibold text-gray-900">Link conversation</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <XIcon className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="px-4 py-3 overflow-y-auto flex-1">
          <p className="text-xs text-gray-500 mb-3">
            {contactInfo} &middot; {channel}
          </p>

          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={() => setAssignTab('existing')}
              className={`px-3 py-1 text-xs font-medium rounded-lg ${
                assignTab === 'existing' ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Add to existing
            </button>
            <button
              type="button"
              onClick={() => setAssignTab('new')}
              className={`px-3 py-1 text-xs font-medium rounded-lg ${
                assignTab === 'new' ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Create new
            </button>
          </div>

          {assignTab === 'existing' ? (
            <div>
              <div className="flex gap-1.5 mb-2">
                <select
                  value={assignType}
                  onChange={(e) => {
                    setAssignType(e.target.value as 'customer' | 'lead')
                    setResults([])
                  }}
                  className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg"
                >
                  <option value="customer">Customer</option>
                  <option value="lead">Lead</option>
                </select>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                  placeholder={`Search ${assignType}s...`}
                  className="flex-1 px-2 py-1.5 text-xs border border-gray-200 rounded-lg"
                />
                <button
                  type="button"
                  onClick={runSearch}
                  disabled={searching}
                  className="px-2 py-1.5 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  {searching ? (
                    <Loader2Icon className="w-3.5 h-3.5 animate-spin text-gray-500" />
                  ) : (
                    <SearchIcon className="w-3.5 h-3.5 text-gray-600" />
                  )}
                </button>
              </div>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {results.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center justify-between bg-gray-50 px-2 py-1.5 rounded-lg border border-gray-100"
                  >
                    <span className="text-xs text-gray-800 truncate">{r.name}</span>
                    <span className="flex items-center gap-2 flex-shrink-0 ml-2">
                      <button
                        type="button"
                        disabled={assigning}
                        onClick={() => onAttachOnly(r, assignType)}
                        title="Link only this conversation"
                        className="text-xs text-gray-600 hover:text-gray-900 font-medium"
                      >
                        Attach only
                      </button>
                      <button
                        type="button"
                        disabled={assigning}
                        onClick={() => onSaveAsContact(r, assignType)}
                        title="Save sender as contact"
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Save as contact
                      </button>
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex items-center flex-wrap gap-1.5 mt-3 pt-3 border-t border-gray-100">
                <span className="text-[11px] text-gray-500">Or link to a</span>
                {([
                  { key: 'property_id' as const, label: 'Property' },
                  { key: 'job_id' as const, label: 'Job' },
                  { key: 'contract_id' as const, label: 'Contract' },
                ]).map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => onLinkEntity(opt.key)}
                    className="px-2 py-0.5 text-[11px] font-medium bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700"
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => onCreateNew('customer')}
                className="flex-1 px-3 py-2 text-xs font-medium bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 text-gray-800"
              >
                New Customer
              </button>
              <button
                type="button"
                onClick={() => onCreateNew('lead')}
                className="flex-1 px-3 py-2 text-xs font-medium bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 text-gray-800"
              >
                New Lead
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
